import { createSelector } from 'reselect';
import { formValueSelector } from 'redux-form/immutable';
import { List } from 'immutable';
import { NDFL_FORM_ID } from 'modules/Taps/components/NDFL3/constants/forms';
import {
  ndfl3PackageFormSelect,
  reportYear,
  selectNDFL3,
} from 'modules/Taps/components/NDFL3/selectors';
import { filter } from 'lodash';
import moment from 'utils/moment';

const EMPTY_LIST = List([]);
const selector = formValueSelector(NDFL_FORM_ID);
export const checks = createSelector(selectNDFL3, ({ checks }) => checks);

export const checkByReportYear = createSelector(checks, reportYear, (checks, reportYear) =>
  filter(checks, (check) => moment(check.originDate).year() === reportYear),
);

export const checkByLessOrEqualReportYear = createSelector(
  checks,
  reportYear,
  (checks, reportYear) => filter(checks, (check) => moment(check.originDate).year() <= reportYear),
);

export const checkByLessOrEqualYear = (reportYear) =>
  createSelector(checks, (checks) =>
    filter(checks, (check) => moment(check.originDate).year() <= reportYear),
  );

export const selfEducationChecks = createSelector(ndfl3PackageFormSelect, (form) =>
  form.getIn(['payload', 'sheetE2', 'charityLiveDeduction', 'selfEducationChecks']),
);

export const treatmentMedicamentChecks = createSelector(ndfl3PackageFormSelect, (form) =>
  form.getIn(['payload', 'sheetE2', 'charityLiveDeduction', 'treatmentMedicamentChecks']),
);

export const expensiveTreatmentChecks = createSelector(ndfl3PackageFormSelect, (form) =>
  form.getIn(['payload', 'sheetE2', 'charityLiveDeduction', 'expensiveTreatmentChecks']),
);
export const nonExpensiveTreatmentChecks = createSelector(ndfl3PackageFormSelect, (form) =>
  form.getIn(['payload', 'sheetE2', 'charityLiveDeduction', 'nonExpensiveTreatmentChecks']),
);

export const insuranceFeeChecks = createSelector(ndfl3PackageFormSelect, (form) =>
  form.getIn(['payload', 'sheetE2', 'charityLiveDeduction', 'insuranceFeeChecks']),
);

export const qualificationChecks = createSelector(ndfl3PackageFormSelect, (form) =>
  form.getIn(['payload', 'sheetE2', 'charityLiveDeduction', 'qualificationChecks']),
);

export const pensContribDeductionChecks = createSelector(ndfl3PackageFormSelect, (form) =>
  form.getIn(['payload', 'sheetE2', 'pensContribDeduction', 'contribChecks']),
);

export const attachedChecks = createSelector(
  ndfl3PackageFormSelect,
  (form) => form.getIn(['payload', 'checks']) || EMPTY_LIST,
);

export const childEducationChecks = createSelector(ndfl3PackageFormSelect, (form) =>
  form.getIn(['payload', 'sheetE2', 'charityLiveDeduction', 'childEducationChecks']),
);

export const commonChildEducationChecks = createSelector(
  childEducationChecks,
  (checkList) => checkList.flatten() || EMPTY_LIST,
);

export const expensesChecks = createSelector(
  ({ state, fieldArrayIndex }) =>
    selector(state, `Ndfl3Package.payload.sheetD1.propertyObjects[${fieldArrayIndex}]`),
  (fieldArrayValue) => (fieldArrayValue && fieldArrayValue.get('expensesChecks')) || null,
);

export const pensInsContracts = createSelector(
  ndfl3PackageFormSelect,
  (form) => form.getIn(['payload', 'sheetE2', 'pensInsDeduction', 'contracts']) || EMPTY_LIST,
);

export const pensInsDeductionCommonChecks = createSelector(pensInsContracts, (contracts) =>
  contracts.map((contractObject) => contractObject.get('contribChecks')).flatten(),
);
export const attachedChecksExcludePensContrib = createSelector(
  attachedChecks,
  pensContribDeductionChecks,
  (attachedChecks, pensContribDeductionChecks) =>
    attachedChecks.filter((check) => !pensContribDeductionChecks.includes(check.get('receiptId'))),
);
export const commonCharityLiveChecks = createSelector(
  selfEducationChecks,
  nonExpensiveTreatmentChecks,
  treatmentMedicamentChecks,
  expensiveTreatmentChecks,
  insuranceFeeChecks,
  qualificationChecks,
  commonChildEducationChecks,
  (
    selfEducationChecks,
    nonExpensiveTreatmentChecks,
    treatmentMedicamentChecks,
    expensiveTreatmentChecks,
    insuranceFeeChecks,
    qualificationChecks,
    commonChildEducationChecks,
  ) =>
    List([])
      .concat([
        commonChildEducationChecks,
        selfEducationChecks,
        nonExpensiveTreatmentChecks,
        treatmentMedicamentChecks,
        expensiveTreatmentChecks,
        insuranceFeeChecks,
        qualificationChecks,
      ])
      .flatten(),
);
export const attachedChecksExcludeCharityLive = createSelector(
  attachedChecks,
  commonCharityLiveChecks,
  (attachedChecks, commonCharityLiveChecks) =>
    attachedChecks.filter((check) => !commonCharityLiveChecks.includes(check.get('receiptId'))),
);
export const attachedChecksExcludePensInsDeduction = createSelector(
  attachedChecks,
  pensInsDeductionCommonChecks,
  (attachedChecks, pensInsDeductionCommonChecks) =>
    attachedChecks.filter(
      (check) => !pensInsDeductionCommonChecks.includes(check.get('receiptId')),
    ),
);
